<script setup>
import { computed, onMounted, onBeforeUnmount, nextTick, ref, watch } from 'vue';
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import apiFetch from '@wordpress/api-fetch';
import { useBusinessModelTypologiesStore } from '../stores/businessModelTypologies';
import { useProcurementTypesStore } from '../stores/procurementTypes';
import { useCitiesStore } from '../stores/cities';
import { useCityPopulationRangesStore } from '../stores/cityPopulationRanges';
import { useCountriesStore } from '../stores/countries';
import Filters from './Filters.vue';
import InfoIcon from '../assets/info-icon.svg?component';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  footnote: {
    type: String,
    default: '',
  },
  data: {
    type: Array,
    default: () => [],
  },
  isColumn: {
    type: Boolean,
    default: false,
  },
  showCountries: {
    type: Boolean,
    default: false,
  },
  emptyMessage: {
    type: String,
    default: '',
  },
});

const businessModelTypologiesStore = useBusinessModelTypologiesStore();
const procurementTypesStore = useProcurementTypesStore();
const countriesStore = useCountriesStore();
const citiesStore = useCitiesStore();
const cityPopulationRangesStore = useCityPopulationRangesStore();
const isFiltersModalOpen = ref(false);
const strings = ref(null);

ChartJS.register(BarElement, CategoryScale, LinearScale, ChartDataLabels);

// When the caller knows the data set is genuinely empty (not still in flight),
// it passes emptyMessage, which swaps the perpetual loading spinner for that
// message instead.
const isLoading = computed(() => props.data.length === 0 && !props.emptyMessage);
const isEmpty = computed(() => props.data.length === 0 && !!props.emptyMessage);
const openMobileTooltip = ref(null);

const chartData = computed(() => ({
  labels: props.data.map((item) => item?.name),
  datasets: [
    {
      data: props.data.map((item) => {
        const value = Number(item?.procurement_count ?? item?.count ?? 0);
        return Number.isFinite(value) ? value : 0;
      }),
      backgroundColor: props.data.map((item) => item?.color || '#1f7eff'),
      barThickness: 22,
      maxBarThickness: 22,
      categoryPercentage: 1,
      barPercentage: 1,
    },
  ],
}));

const chartOptions = computed(() => ({
  indexAxis: 'y',
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
    },
    datalabels: {
      anchor: 'end',
      align: 'right',
      offset: 8,
      color: '#212529',
      font: { family: 'IBM Plex Mono', size: 16, weight: 'normal' },
      formatter: (value) => `${value}`,
    },
  },
  scales: {
    x: {
      min: 0,
      beginAtZero: true,
      max: 100,
      grid: { display: false },
      border: { display: false },
      ticks: { display: false },
    },
    y: {
      offset: true,
      grid: { display: false },
      border: { display: false },
      ticks: { display: false },
    },
  },
}));

const chartOptionsNoLabel = computed(() => ({
  ...chartOptions.value,
  plugins: {
    ...chartOptions.value.plugins,
    datalabels: { display: false },
  },
}));

const title = computed(() => {
  if (!props.footnote) {
    return props.title;
  }

  return `${props.title}*`;
});

const titleTooltip = computed(() => {
  if (!strings.value) {
    return '';
  }

  const tooltipByTitle = [
    { title: strings.value.procurement_type, tooltip: strings.value.procurement_type_tooltip },
    { title: strings.value.business_model_typology, tooltip: strings.value.business_model_typology_tooltip },
    { title: strings.value.governance_arrangement_level, tooltip: strings.value.governance_level_tooltip },
    { title: strings.value.contract_duration, tooltip: strings.value.contract_duration_tooltip },
    { title: strings.value.electric_requirements, tooltip: strings.value.electric_requirement_tooltip },
    { title: strings.value.payment_or_revenue_sources, tooltip: strings.value.payment_or_revenue_source_tooltip },
  ];

  return tooltipByTitle.find((item) => item.title === props.title)?.tooltip || '';
});

function toggleMobileTooltip(id, event) {
  const wrapper = event?.currentTarget?.closest('.tt-wrapper');
  openMobileTooltip.value = openMobileTooltip.value === id ? null : id;

  if (openMobileTooltip.value === id) {
    nextTick(() => repositionTooltip(wrapper));
  }
}

// The tooltip box is centered on its info icon via -translate-x-1/2, which
// clips off-screen when the icon is close to the left or right edge of the
// viewport. There's no CSS-only way to know where the icon sits relative to
// the viewport, so on show we measure it and, if it would clip, snap it to
// whichever corner of the icon keeps it on-screen instead of centering it.
// Note: Tailwind v4 compiles -translate-x-1/2 to the standalone CSS
// `translate` property, not `transform`, so that's what has to be overridden.
function repositionTooltip(wrapper) {
  const box = wrapper?.querySelector('.tt-box');
  if (!box) {
    return;
  }

  box.style.removeProperty('left');
  box.style.removeProperty('right');
  box.style.removeProperty('translate');

  const rect = box.getBoundingClientRect();
  const margin = 16;

  if (rect.right > window.innerWidth - margin) {
    box.style.setProperty('left', 'auto', 'important');
    box.style.setProperty('right', '0', 'important');
    box.style.setProperty('translate', 'none', 'important');
  } else if (rect.left < margin) {
    box.style.setProperty('left', '0', 'important');
    box.style.setProperty('translate', 'none', 'important');
  }
}

const columnChartData = computed(() => {
  return (chartData.value.labels || []).map((label, index) => ({
    labels: [label],
    datasets: [
      {
        data: [Number((chartData.value.datasets?.[0]?.data)[index])],
        backgroundColor: [(chartData.value.datasets?.[0]?.backgroundColor)[index] || '#1f7eff'],
        barThickness: 40,
        maxBarThickness: 40,
        minBarLength: 0,
        categoryPercentage: 1,
        barPercentage: 1,
      },
    ],
  }));
});

const desktopRowChartData = computed(() => {
  return (chartData.value.labels || []).map((label, index) => ({
    labels: [label],
    datasets: [
      {
        data: [Number((chartData.value.datasets?.[0]?.data)[index])],
        backgroundColor: [(chartData.value.datasets?.[0]?.backgroundColor)[index] || '#1f7eff'],
        barThickness: 22,
        maxBarThickness: 22,
        minBarLength: 0,
        categoryPercentage: 1,
        barPercentage: 1,
      },
    ],
  }));
});

function openFiltersModal() {
  isFiltersModalOpen.value = true;
}

function closeFiltersModal() {
  isFiltersModalOpen.value = false;
}

function resetFilters() {
  countriesStore.resetActiveCountries();
  citiesStore.resetActiveCities();
  cityPopulationRangesStore.resetActiveRanges();
  procurementTypesStore.resetActiveProcurementTypes();
  businessModelTypologiesStore.resetActiveBusinessModelTypologies();
}

watch(isFiltersModalOpen, (isOpen) => {
  document.documentElement.style.overflow = isOpen ? 'hidden' : '';
  document.body.style.overflow = isOpen ? 'hidden' : '';
});

onBeforeUnmount(() => {
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
});

onMounted(async () => {
  try {
    const lang = document.documentElement.lang?.substring(0, 2);
    const translations = await apiFetch({
      path: `/wp-json/ebus/market/translations?lang=${lang}`,
    });

    strings.value = translations;
  } catch (error) {
    console.error('Error: ', error);
  }
});
</script>

<template>
  <div v-if="strings" class="flex! flex-col! gap-y-3!">
    <div class="inline-flex! items-center! gap-2!">
      <h3 :class="`font-ibm-plex-mono! font-medium! leading-tight! text-blue! ${props.isColumn ? 'text-base!' : 'text-xl!'}`">{{ title }}</h3>
      <div
        v-if="titleTooltip"
        :class="props.isColumn ? 'tt-wrapper relative! inline-flex! items-center!' : 'relative! inline-flex! items-center! group'"
      >
        <button
          v-if="props.isColumn"
          type="button"
          class="inline-flex! items-center! p-0! border-0! bg-transparent!"
          @click="toggleMobileTooltip(`${title}-title`, $event)"
        >
          <InfoIcon class="w-4! h-4! [&>path]:fill-black!" />
        </button>
        <InfoIcon v-else class="w-4! h-4! [&>path]:fill-black!" />
        <span
          v-if="props.isColumn ? openMobileTooltip === `${title}-title` : true"
          :class="props.isColumn ? 'tt-box absolute! left-1/2! top-full! z-50! mt-1! w-72! max-w-[calc(100vw-4rem)]! -translate-x-1/2! bg-blue! px-3! py-2! font-ibm-plex-mono! font-normal! text-xs! leading-tight! text-white!' : 'pointer-events-none! absolute! right-0! top-full! z-50! mt-1! w-85.5! bg-blue! px-4! py-3! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-white! opacity-0! transition-opacity! group-hover:opacity-100!'"
        >
          {{ titleTooltip }}
        </span>
      </div>
    </div>

    <div
      v-if="!isLoading && !isEmpty"
      :class="props.isColumn ? 'flex! flex-col! gap-y-3!' : 'flex! flex-col! gap-y-0!'"
    >
      <div :class="props.isColumn ? 'w-full! flex! flex-col! gap-y-3!' : 'w-full! flex! flex-col!'">
        <div
          v-for="(item, index) in props.data"
          :key="item.id || item.slug || item.name"
          :class="props.isColumn ? 'flex! flex-col! gap-y-1!' : 'flex! flex-col! lg:flex-row! lg:items-center! gap-y-1.5! lg:gap-y-0! lg:gap-x-2.5! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! py-1!'"
        >
          <div :class="props.isColumn ? 'inline-flex! items-center! gap-1!' : 'flex! items-center! gap-2! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! lg:w-96! lg:min-w-96! lg:flex-shrink-0!'">
            <span :class="props.isColumn ? 'font-ibm-plex-mono! font-medium! text-base! leading-tight! text-black!' : 'wrap-break-word!'">{{ item?.name }}</span>

            <div
              v-if="item?.description"
              :class="props.isColumn ? 'tt-wrapper relative! inline-flex! items-center!' : 'tt-wrapper relative! inline-flex! items-center! group'"
              @mouseenter="!props.isColumn && repositionTooltip($event.currentTarget)"
            >
              <button
                v-if="props.isColumn"
                type="button"
                class="inline-flex! items-center! p-0! border-0! bg-transparent!"
                @click="toggleMobileTooltip(`${title}-${item.id || item.slug || item.name}`, $event)"
              >
                <InfoIcon class="w-4! h-4!" />
              </button>
              <InfoIcon v-else class="w-4! h-4!" />
              <span :class="props.isColumn ? 'tt-box absolute! left-1/2! top-full! z-50! mt-1! w-72! max-w-[calc(100vw-4rem)]! -translate-x-1/2! bg-blue! px-3! py-2! font-ibm-plex-mono! font-normal! text-xs! leading-tight! text-white!' : 'tt-box pointer-events-none! absolute! left-1/2! top-full! z-50! mt-1! w-85.5! -translate-x-1/2! bg-blue! px-4! py-3! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-white! opacity-0! transition-opacity! group-hover:opacity-100!'"
                v-if="props.isColumn ? openMobileTooltip === `${title}-${item.id || item.slug || item.name}` : true"
              >
                {{ item.description }}
              </span>
            </div>
          </div>

          <div :class="props.isColumn ? 'flex! items-center! w-full! gap-x-2!' : 'lg:hidden! flex-1! min-w-0! h-4!'">
            <template v-if="props.isColumn">
              <div
                class="flex-shrink-0! w-1/2! -ml-2!"
                :style="{ height: '40px' }"
              >
                <Bar
                  :data="columnChartData[index]"
                  :options="chartOptions"
                />
              </div>
              <span v-if="props.showCountries" class="flex-grow! font-ibm-plex-mono! font-leight! text-base! leading-tight! text-black! opacity-75! uppercase!">
                <template v-if="item.countries?.length">{{ item.countries.join(', ') }}</template>
              </span>
            </template>
            <div v-else class="w-full! h-full!">
              <Bar
                :data="columnChartData[index]"
                :options="chartOptions"
              />
            </div>
          </div>

          <div v-if="!props.isColumn" class="hidden! lg:flex! flex-1! min-w-0! items-center! gap-x-3!">
            <div class="flex-shrink-0! w-1/3!" style="height: 32px">
              <Bar
                :data="desktopRowChartData[index]"
                :options="chartOptions"
              />
            </div>
            <span v-if="props.showCountries" class="flex-grow! font-ibm-plex-mono! font-light! text-base! leading-tight! text-black! opacity-75! uppercase!">
              <template v-if="item.countries?.length">{{ item.countries.join(', ') }}</template>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="isEmpty" class="py-6! text-center!">
      <span class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">
        {{ props.emptyMessage }}
      </span>
    </div>

    <div v-else class="px-6! py-12! text-center! flex! items-center! justify-center!">
      <span class="w-12! h-12! border-4! border-blue! border-t-transparent! rounded-full! animate-spin!"></span>
    </div>

    <p v-if="props.footnote" class="font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-black! mt-5!">
      {{ props.footnote }}
    </p>

    <div v-if="!isLoading && !isFiltersModalOpen" class="lg:hidden! mt-2! flex! justify-end!">
      <button
        type="button"
        class="font-ibm-plex-mono! text-sm! leading-tight! text-black! lowercase! border! border-black! rounded-full! px-3.5! py-0.5!"
        @click="openFiltersModal"
      >
        {{ strings.show_filters }}
      </button>
    </div>

    <div v-if="isFiltersModalOpen" class="lg:hidden! fixed! inset-0! z-50! bg-light-blue-3! overflow-hidden!">
      <div class="w-full! h-full! bg-light-blue-3! pt-8.5! px-6! pb-6! flex! flex-col! gap-y-4!">
        <Filters />

        <div class="flex! items-center! gap-x-6! pt-8.5!">
          <button
            type="button"
            class="font-ibm-plex-mono! text-sm! leading-tight! text-black! lowercase! border! border-black! rounded-full! px-3.5! py-0.5!"
            @click="closeFiltersModal"
          >
            {{ strings.close_filters }}
          </button>

          <button
            type="button"
            class="font-ibm-plex-mono! text-sm! leading-tight! text-black! lowercase! border! border-black! rounded-full! px-3.5! py-0.5!"
            @click="resetFilters"
          >
            {{ strings.reset_filters }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
